import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.js'

export const workspacePlanSchema = z.enum([
  'free',
  'individual',
  'enterprise',
])
export type WorkspacePlan = z.infer<typeof workspacePlanSchema>

export const workspaceSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten'),
  plan: workspacePlanSchema,
  default_model: z.string().min(1),
  fallback_model: z.string().nullable(),
  monthly_token_quota: z.number().int().nonnegative().nullable(),
  brand_color_primary: z.string().nullable(),
  brand_color_accent: z.string().nullable(),
  custom_domain: z.string().nullable(),
  stripe_customer_id: z.string().nullable(),
  stripe_subscription_id: z.string().nullable(),
  quota_monthly_eur: z.number().nullable().optional(),
  quota_period_start: z.string().optional(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
})
export type Workspace = z.infer<typeof workspaceSchema>

export const workspaceCreateSchema = workspaceSchema
  .pick({ name: true, slug: true })
  .extend({
    default_model: z.string().min(1).optional(),
  })
export type WorkspaceCreate = z.infer<typeof workspaceCreateSchema>

export const workspaceUpdateSchema = workspaceSchema
  .pick({
    name: true,
    default_model: true,
    fallback_model: true,
    monthly_token_quota: true,
    brand_color_primary: true,
    brand_color_accent: true,
    custom_domain: true,
  })
  .partial()
export type WorkspaceUpdate = z.infer<typeof workspaceUpdateSchema>

export const workspaceMemberRoleSchema = z.enum([
  'owner',
  'admin',
  'member',
  'viewer',
])
export type WorkspaceMemberRole = z.infer<typeof workspaceMemberRoleSchema>

/** Canonical header name for the active workspace (D-S1B2a-1). */
export const WORKSPACE_ID_HEADER = 'x-workspace-id'

export const myWorkspaceSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(120),
  slug: z.string().min(1).max(60),
  role: workspaceMemberRoleSchema,
})
export type MyWorkspace = z.infer<typeof myWorkspaceSchema>

export const myWorkspacesResponseSchema = z.object({
  workspaces: z.array(myWorkspaceSchema),
})
export type MyWorkspacesResponse = z.infer<typeof myWorkspacesResponseSchema>

export const workspaceMemberSchema = z.object({
  workspace_id: uuidSchema,
  user_id: uuidSchema,
  role: workspaceMemberRoleSchema,
  invited_by: uuidSchema.nullable(),
  invited_at: timestampSchema.nullable(),
  joined_at: timestampSchema.nullable(),
  created_at: timestampSchema,
})
export type WorkspaceMember = z.infer<typeof workspaceMemberSchema>

export const workspaceSettingSchema = z.object({
  workspace_id: uuidSchema,
  key: z.string().min(1).max(120),
  value: z.unknown(),
  updated_at: timestampSchema,
})
export type WorkspaceSetting = z.infer<typeof workspaceSettingSchema>

/** Deployment-Profil (D-020 / D-S1A-1): eine Werteliste, keine Forks. */
export const deploymentProfileSchema = z.enum(['personal', 'team', 'private', 'onprem'])
export type DeploymentProfile = z.infer<typeof deploymentProfileSchema>
export const DEFAULT_DEPLOYMENT_PROFILE: DeploymentProfile = 'team'
export const FAIL_CLOSED_DEPLOYMENT_PROFILE: DeploymentProfile = 'onprem'
